import { Field } from "../Field";
import { Spacer } from "../common/Spacer";
import { WizzardButton } from "../WizzardButton";
import {
	generateCustomerFormsData,
	generateFormsPayload,
} from "lib/generateFormsPayload";
import { getInputNameByQuestion } from "lib/getInputNameByQuestion";
import { getSessionId } from "lib/sessionIdTemporaryUser";
import { pathOr } from "ramda";
import { useAuth } from "hooks/useAuth";
import { useForm } from "react-hook-form";
import React, { useDispatch, useEffect, useGlobal, useState } from "reactn";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const defaultFields = [
	{
		_id: "618d873c5650eb29d844f1e4",
		officeId: 1777,
		fieldType: "name",
		question: "Nombre",
		answer: "",
		limit: 32,
		required: true,
		validCharacter: "[aA-zZ]",
		validMessage: "Debe ingresar un nombre válido",
		configId: {},
		active: true,
		created_at: "2021-11-11T21:12:28.432Z",
		__v: 0,
	},
	{
		_id: "618d873c5650eb29d834f1e3",
		officeId: 1777,
		fieldType: "tel",
		question: "Teléfono",
		answer: "",
		limit: 60,
		required: true,
		validCharacter: "^[0-9]*$",
		validMessage: "Debe ingresar un numero de teléfono válido",
		configId: {},
		active: true,
		created_at: "2021-11-11T21:12:28.431Z",
		__v: 0,
	},
	{
		_id: "618d873c5650eb29d14f1e5",
		officeId: 1777,
		fieldType: "email",
		question: "Email",
		answer: "",
		required: true,
		validCharacter: "[aA-zZ]",
		validMessage: "Debe ingresar un correo válido",
		configId: {},
		active: true,
		created_at: "2021-11-11T21:12:28.433Z",
		__v: 0,
	},
];

export const Forms = ({
	desktop,
	error,
	formData,
	handleReservation,
	isDniRequired,
	isLoggedInTemporary,
	isRutRequired,
	loading,
	officeSlug,
	onSubmitWithReCAPTCHA,
	setIsRutModalVisible,
	setReservationPayload,
	temporaryDni,
	temporaryQueryParam,
	temporaryRut,
	wantTemporary,
}) => {
	const setFormsPayload = useDispatch("reservationsFormsPayload");
	const [forms] = useGlobal("forms");
	const { t } = useTranslation("booking");
	const [customerParams] = useGlobal("customerParams");
	const formType = pathOr("default", ["reservationForms", "type"], forms);
	const [localQuestions, setLocalQuestions] = useState([]);
	const [offices] = useGlobal("officesv2");
	const office = offices.bySlug[officeSlug];
	const [phoneCode, setPhoneCode] = useState("+56");
	const [countryCode, setCountryCode] = useState(
		pathOr("cl", ["territory"], office)
	);
	const [localMeta, setLocalMeta] = useState({});
	const [user] = useGlobal("user");
	const userEmail = user?.email?.includes("@");
	const { isLoggedIn } = useAuth();

	const modalNedded = isRutRequired || isDniRequired;

	const {
		register,
		handleSubmit,
		formState: { errors },
		control,
		watch,
	} = useForm({
		mode: "onChange",
		shouldUnregister: true,
		shouldUseNativeValidation: false,
	});

	useEffect(() => {
		if (countryCode.length > 2) {
			switch (countryCode) {
				case "Argentina":
					setCountryCode("ar");
					break;
				case "Bolivia":
					setCountryCode("bo");
					break;
				case "Brasil":
					setCountryCode("br");
					break;
				case "Chile":
					setCountryCode("cl");
					break;
				case "Colombia":
					setCountryCode("co");
					break;
				case "Ecuador":
					setCountryCode("ec");
					break;
				case "México":
					setCountryCode("mx");
					break;
				case "Perú":
					setCountryCode("pe");
					break;
				case "Uruguay":
					setCountryCode("ur");
					break;
				default:
					setCountryCode("cl");
			}
		}
	}, [countryCode]);

	useEffect(() => {
		const questions = formData.length > 0 ? formData : defaultFields;
		const fieldPhone = questions.find(
			(item) => item.question === `Teléfono`
		);
		const subscription = watch((value, { name }) => {
			if (name === fieldPhone?._id) {
				const phone = value[name] ?? ``;
				setPhoneCode(`+${phone.substring(0, 2)}`);
			}
		});
		return () => subscription.unsubscribe();
	}, [formData, watch]);

	const loginAsTemporary = ({ questions, payload, rut, dni }) => {
		const result = getInputNameByQuestion(questions);
		getSessionId(officeSlug).then((res) => {
			const string = res.toString();
			//add rut or dni to payload

			const payloadToLogin = {
				["sessionId"]: string,
				...result,
			};
			if (rut) {
				payloadToLogin["rut"] = rut;
			}
			if (dni) {
				payloadToLogin["dni"] = dni;
			}

			return onSubmitWithReCAPTCHA(payloadToLogin, payload);
		});
	};

	// esta es la func que manda la info al back
	const onSubmit = (data) => {
		delete data.checkbox;
		const questions = generateFormsPayload(
			data,
			formData.length > 0 ? formData : defaultFields
		);

		const payload = {
			forms: {
				type: formType,
				questions,
			},
		};

		setReservationPayload(payload);
		setFormsPayload(payload);
		setLocalQuestions(questions);
		setLocalMeta(payload);

		if (modalNedded && isLoggedIn) {
			return setIsRutModalVisible(true);
		}

		if (temporaryQueryParam || (!userEmail && isLoggedInTemporary)) {
			loginAsTemporary({ questions, payload });
			return;
		}

		handleReservation(payload);
	};

	useEffect(() => {
		if (wantTemporary) {
			loginAsTemporary({
				questions: localQuestions,
				payload: localMeta,
				rut: temporaryRut,
				dni: temporaryDni,
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [wantTemporary]);

	return (
		<FormStyled
			desktop={desktop}
			onSubmit={handleSubmit(onSubmit)}
			id={"dynamicForm"}
		>
			{!desktop && (
				<div className={"dynamic-form-title"}>{t("titleForm")}</div>
			)}
			<Spacer rows={1} />
			{!formData.length &&
				defaultFields.map((form, index) => {
					return (
						<Field
							phoneCode={phoneCode}
							setPhoneCode={setPhoneCode}
							key={form._id}
							{...form}
							register={register}
							defaultField
							errors={errors}
							control={control}
							countryCode={countryCode}
						/>
					);
				})}

			{formData.length > 0 &&
				formData.map((form, index) => {
					return (
						<Field
							phoneCode={phoneCode}
							setPhoneCode={setPhoneCode}
							errors={errors}
							key={form._id}
							{...form}
							register={register}
							control={control}
							countryCode={countryCode}
						/>
					);
				})}

			{error && error.message && (
				<div className="error-label">{error.message}</div>
			)}
			<Spacer rows={1} />

			<ButtonStyled desktop={desktop}>
				<WizzardButton
					label={t("completeForm")}
					type="primary"
					loading={loading}
					typeInput="submit"
					disabled={loading}
				/>
			</ButtonStyled>
		</FormStyled>
	);
};

const ButtonStyled = styled.div`
	width: 200px;
	position: absolute;
	bottom: ${(props) => (props.desktop ? "-60px" : "-86px")};
	left: ${(props) => (props.desktop ? "160px" : "124px")};
`;

const FormStyled = styled.form`
	width: 100%;
	position: relative;
	/* display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center; */
	.dynamic-form-title {
		font-weight: 700;
	}
	.error-label {
		font-size: 12px;
		color: #ff4d4f;
	}
`;
